import { useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { PhoneActions, PhoneScreen } from '@/components/kit';
import type { ShellChrome } from './shellChrome';
import { useAuthStore } from '@/stores/useAuthStore';
import type { SidebarItem } from '@/config/navigation';

/**
 * EVERY PAGE IS USABLE ON A PHONE, PORTED OR NOT.
 *
 * `ShellChrome.phone` lets a page draw its own phone screen, and the ones that
 * do are better than this. But a page that has NOT drawn one used to get the
 * desk shell at 390px: a 210px rail eating half the width, the content squeezed
 * into what was left, and a docked pane clipped off the edge. That is not an
 * un-ported page, it is a broken one — and there are seventy of them.
 *
 * So the shell falls back instead of failing: the routed page's own content,
 * full width, under the same top strip every phone board draws, with ☰ opening
 * the rail's own doors. Nothing is invented and nothing is hidden — it is the
 * page as written, given the whole screen.
 *
 * THE MENU IS THE RAIL, not a second navigation. Whatever doors this role holds
 * on the desk are the doors here, read from the same `items` the rail is built
 * from, so the two can never disagree about where somebody can go.
 *
 * A LANE MAY HAND OVER ITS OWN ☰ instead, through `chrome.phoneMenu`. The
 * rail's doors are the right SET, but a designed lane menu also carries each
 * door's live count, the "Home" chip and the line about what deliberately does
 * not open from there — and that costs the page one line of chrome rather than
 * a whole phone board.
 *
 * THE TOP BAR'S TWO WORKING PARTS COME WITH IT. On the desk a page's one
 * primary ("Save", "+ Book", "Top up") and its search field live in the top
 * bar, and the phone strip used to drop both — a manager could open Facility
 * on a phone, edit every field and have no way to save. The primary rides the
 * strip's trailing slot, as the phone boards draw their one action; a live
 * search sits at the head of the list, where every phone board puts it.
 * The primary comes FIRST, then the secondary — the order the phone boards
 * draw (g_f1: "Continue visit · draft", then "Issue ▾").
 *
 * AND THE TOP BAR'S `extra` — "⚠ unsaved", "Change payment", "Enable
 * subdomain" — used to be dropped outright, so a control the desk had simply
 * did not exist on a phone. It cannot share a 390px strip with a title and a
 * primary (some are full-size buttons), so it is a row at the head of the
 * content, right-aligned and wrapping. A page that wants a control IN the
 * strip — a camera icon — publishes it as `secondary` instead.
 */
export function ShellPhoneFallback({
  items,
  title,
  sub,
  menu,
  chrome,
  children,
}: {
  items: SidebarItem[];
  title: ReactNode;
  sub?: ReactNode;
  menu?: (close: () => void) => ReactNode;
  chrome?: ShellChrome;
  children: ReactNode;
}) {
  const primary = chrome?.primary;
  /* Under a crumb the strip holds the place's name; a second chip squeezed
     "Expenses ▸ Recurring" down to one letter. The primary stays. */
  const secondary = chrome?.phoneCrumb ? undefined : chrome?.secondary;
  const trailing = chrome?.phoneTrailing ?? (primary || secondary
    ? <PhoneActions primary={primary} secondary={secondary} />
    : undefined);
  const extraRow = chrome?.extra ? <div className="kit-phone-extra">{chrome.extra}</div> : null;

  const onSearchChange = chrome?.onSearchChange;
  const search = chrome?.search && onSearchChange && chrome.phoneSearch !== false
    ? { placeholder: chrome.search, value: chrome.searchValue ?? '', onChange: onSearchChange }
    : undefined;

  return (
    <PhoneScreen
      title={chrome?.phoneTitle ?? title}
      sub={chrome?.phoneCrumb ? chrome.phoneSub : sub}
      crumb={chrome?.phoneCrumb}
      onBack={chrome?.onPhoneBack}
      trailing={trailing}
      search={search}
      menu={menu ?? ((close) => <RailMenu items={items} onClose={close} />)}
    >
      {extraRow}
      {children}
    </PhoneScreen>
  );
}

/** The rail's doors as a phone sheet — groups flattened under their heading. */
function RailMenu({ items, onClose }: { items: SidebarItem[]; onClose: () => void }) {
  const navigate = useNavigate();
  const location = useLocation();
  const logout = useAuthStore((s) => s.logout);
  const user = useAuthStore((s) => s.user);
  const [closing, setClosing] = useState(false);

  const go = (path?: string) => {
    if (!path) return;
    onClose();
    navigate(path);
  };
  const current = (path?: string) =>
    !!path && (location.pathname === path || location.pathname.startsWith(`${path}/`));

  return (
    <div className="maie desk kit-modal-dock">
      <div className="dscrim" onClick={onClose} role="presentation" />
      <div className="burger" role="dialog" aria-modal="true" aria-label="Menu">
        <div className="topstrip">
          <button type="button" className="ts-btn" onClick={onClose} aria-label="Close menu">
            ✕
          </button>
          <span className="grow" />
          {user?.primaryRole ? (
            <span className="chip chip-soft chip-sm">{String(user.primaryRole).replace(/_/g, ' ')}</span>
          ) : null}
        </div>

        <div className="scroll">
          {items.map((item) =>
            item.children?.length ? (
              <div key={item.id}>
                <p className="t-ovl mut" style={{ margin: '14px 0 4px' }}>{item.label}</p>
                {item.children.map((c) => (
                  <MenuRow key={c.id} item={c} on={current(c.path)} onSelect={() => go(c.path)} />
                ))}
              </div>
            ) : (
              <MenuRow key={item.id} item={item} on={current(item.path)} onSelect={() => go(item.path)} />
            ),
          )}

          <hr className="hr" />
          <MenuRow
            item={{ id: 'settings', label: 'Settings', icon: () => <span /> } as SidebarItem}
            on={current('/settings')}
            onSelect={() => go('/settings')}
          />
          <button
            type="button"
            className="row card-btn"
            disabled={closing}
            onClick={() => { setClosing(true); void logout(); }}
          >
            <span className="grow">
              <span className="t-strong">Sign out</span>
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}

function MenuRow({
  item,
  on,
  onSelect,
}: {
  item: SidebarItem;
  on: boolean;
  onSelect: () => void;
}) {
  const Icon = item.icon;
  return (
    <button type="button" className="row card-btn" onClick={onSelect}>
      <span style={{ display: 'inline-flex', width: 24, flexShrink: 0 }}>
        <Icon className="ic" />
      </span>
      <span className="grow" style={{ minWidth: 0 }}>
        <span className="t-strong">{item.label}</span>
      </span>
      {on ? <span className="chip chip-brand chip-sm">Home</span> : null}
      {item.badge ? <span className="chip chip-outline chip-sm">{item.badge}</span> : null}
    </button>
  );
}
